import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { AlertTriangle, Loader2, Radio } from "lucide-react";

function timeParts(ms) {
  if (ms < 0) ms = 0;
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return { hours, minutes, seconds };
}

/**
 * Full-screen maintenance overlay. Auto-shows during a scheduled window
 * defined by data.dateISO -> data.endISO, and auto-hides when the window ends.
 * Checks every second. Client-side clock only.
 */
export default function MaintenanceScreen({ data }) {
  const start = new Date(data.dateISO);
  const end = new Date(data.endISO);

  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const isMaintenance = now >= start && now < end;

  useEffect(() => {
    if (isMaintenance) {
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    };
  }, [isMaintenance]);

  if (!isMaintenance) return null;

  const remaining = timeParts(end - now);
  const totalMs = end - start;
  const elapsedMs = Math.max(0, Math.min(totalMs, now - start));
  const progressPct = totalMs > 0 ? (elapsedMs / totalMs) * 100 : 0;

  const m = data.maintenance || {};

  return (
    <motion.div
      data-testid="maintenance-screen"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 z-[500] bg-[#1A1A1F] flex items-center justify-center overflow-hidden"
    >
      {/* Backdrop stripes */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage:
            "repeating-linear-gradient(45deg, #FACC15 0, #FACC15 12px, transparent 12px, transparent 42px)",
        }}
      />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(250,204,21,0.10),transparent_60%)] pointer-events-none" />

      <div className="relative max-w-3xl w-full mx-auto px-6 md:px-10 py-10">
        {/* Icon */}
        <motion.div
          initial={{ scale: 0.7, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="mb-8 flex items-center gap-4"
        >
          <span className="relative flex items-center justify-center w-16 h-16 border border-accent/60 bg-black/60">
            <AlertTriangle size={26} className="text-accent" />
            <span className="absolute inset-0 border border-accent/40 animate-pulse" />
          </span>
          <div className="flex flex-col gap-1">
            <span className="font-body text-[10px] uppercase tracking-[0.35em] text-accent">
              {m.eyebrow || "Manutenzione"}
            </span>
            <span className="font-body text-[10px] uppercase tracking-[0.3em] text-white/60 flex items-center gap-2">
              <Loader2 size={11} className="text-white/60 animate-spin" />
              Sistema aggiornamento in corso
            </span>
          </div>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.7 }}
          className="font-heading uppercase text-white text-4xl md:text-6xl lg:text-7xl leading-[0.9] mb-6"
        >
          {m.title || "Sito in manutenzione."}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35, duration: 0.7 }}
          className="font-body text-base md:text-lg text-white/70 leading-relaxed max-w-xl mb-10"
        >
          {m.body}
        </motion.p>

        {/* Progress bar + remaining */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.7 }}
          className="border border-white/15 bg-black/70 backdrop-blur-md p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Radio size={12} className="text-accent" />
              <span className="font-body text-[10px] uppercase tracking-[0.3em] text-accent">
                Tempo restante
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
              <span className="font-body text-[10px] uppercase tracking-[0.3em] text-white/60">
                Live
              </span>
            </div>
          </div>
          <div className="font-heading uppercase text-white text-4xl md:text-6xl leading-none mb-5 tabular-nums">
            {String(remaining.hours).padStart(2, "0")}:
            {String(remaining.minutes).padStart(2, "0")}:
            {String(remaining.seconds).padStart(2, "0")}
          </div>
          {/* Progress bar */}
          <div className="w-full h-1.5 bg-white/10 overflow-hidden">
            <motion.div
              animate={{ width: `${progressPct}%` }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="h-full bg-accent"
            />
          </div>
          <p className="mt-5 font-body text-[10px] uppercase tracking-[0.25em] text-white/40">
            {m.tip}
          </p>
        </motion.div>

        <div className="mt-8 flex items-center gap-3 font-body text-[10px] uppercase tracking-[0.3em] text-white/40">
          <span className="w-8 h-px bg-white/20" />
          Ashvolt Studios · {data.dateLabel}
        </div>
      </div>
    </motion.div>
  );
}
